/**
 * Pure transform edits behind the editor's drag handles. Everything works in
 * the item's parent space and is expressed through computeItemLayout, so the
 * result is exactly what the compositor will draw.
 */
import { applyToPoint, computeItemLayout } from '../../core/geometry';
import type { Crop, ItemTransform, Vec2 } from '../../core/model';

/** Unit-square coordinates of a handle: 0, 0.5 or 1 on each axis. */
export interface Handle {
  x: 0 | 0.5 | 1;
  y: 0 | 0.5 | 1;
}

export const HANDLES: readonly Handle[] = [
  { x: 0, y: 0 }, { x: 0.5, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 0.5 },
  { x: 1, y: 1 }, { x: 0.5, y: 1 }, { x: 0, y: 1 }, { x: 0, y: 0.5 },
];

const MIN_SIZE = 1;
export const ROTATION_SNAP_DEGREES = 15;

function unrotate(v: Vec2, degrees: number): Vec2 {
  const rad = (-degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return { x: v.x * cos - v.y * sin, y: v.x * sin + v.y * cos };
}

export function normalizeDegrees(degrees: number): number {
  const wrapped = ((degrees % 360) + 360) % 360;
  // Trigonometry noise such as 359.99999999 should read as 0.
  const rounded = Number(wrapped.toFixed(4));
  return rounded === 360 ? 0 : rounded;
}

/** Position of a handle (unit coordinates) in parent space. */
export function handlePosition(transform: ItemTransform, size: Vec2, handle: Handle): Vec2 {
  return applyToPoint(computeItemLayout(transform, size).boxMatrix, handle);
}

/** Shifts `position` so that the given unit point of the box stays where it was before the edit. */
function pinPoint(start: ItemTransform, next: ItemTransform, size: Vec2, unit: Vec2): Vec2 {
  const before = applyToPoint(computeItemLayout(start, size).boxMatrix, unit);
  const after = applyToPoint(computeItemLayout(next, size).boxMatrix, unit);
  return { x: next.position.x + before.x - after.x, y: next.position.y + before.y - after.y };
}

function keepSign(value: number, reference: number): number {
  if (Math.abs(value) >= MIN_SIZE) return value;
  return (value < 0 || (value === 0 && reference < 0) ? -1 : 1) * MIN_SIZE;
}

export interface ResizeOptions {
  /** Corner handles scale both axes by the same factor. */
  keepAspect: boolean;
}

/**
 * Drag a handle to `pointer` (parent space) with the opposite handle fixed.
 * Without bounds the scale changes (and may flip); with bounds the bounding
 * box is resized instead.
 */
export function resizeTransform(
  start: ItemTransform,
  size: Vec2,
  handle: Handle,
  pointer: Vec2,
  options: ResizeOptions,
): Partial<ItemTransform> {
  const layout = computeItemLayout(start, size);
  const anchorUnit = { x: 1 - handle.x, y: 1 - handle.y };
  const anchor = applyToPoint(layout.boxMatrix, anchorUnit);
  const local = unrotate({ x: pointer.x - anchor.x, y: pointer.y - anchor.y }, start.rotation);
  const box = layout.boxSize;

  let width = handle.x === 0.5 ? box.x : local.x / (2 * handle.x - 1);
  let height = handle.y === 0.5 ? box.y : local.y / (2 * handle.y - 1);
  const corner = handle.x !== 0.5 && handle.y !== 0.5;
  if (corner && options.keepAspect && box.x !== 0 && box.y !== 0) {
    const factor = Math.max(width / box.x, height / box.y);
    width = box.x * factor;
    height = box.y * factor;
  }

  const usesBounds = start.boundsType !== 'none' && layout.contentSize.x > 0 && layout.contentSize.y > 0;
  const next: ItemTransform = { ...start };
  if (usesBounds) {
    next.bounds = { x: Math.max(MIN_SIZE, Math.abs(width)), y: Math.max(MIN_SIZE, Math.abs(height)) };
  } else {
    const content = layout.contentSize;
    if (content.x <= 0 || content.y <= 0) return {};
    next.scale = { x: keepSign(width, box.x) / content.x, y: keepSign(height, box.y) / content.y };
  }
  next.position = pinPoint(start, next, size, anchorUnit);
  return usesBounds ? { bounds: next.bounds, position: next.position } : { scale: next.scale, position: next.position };
}

/**
 * Alt-drag a handle: move the crop of the edges that handle touches by the
 * pointer travel `from` -> `to` (parent space). The opposite edges stay put.
 */
export function cropTransform(start: ItemTransform, size: Vec2, handle: Handle, from: Vec2, to: Vec2): Partial<ItemTransform> {
  const layout = computeItemLayout(start, size);
  if (layout.scale.x === 0 || layout.scale.y === 0) return {};
  const local = unrotate({ x: to.x - from.x, y: to.y - from.y }, start.rotation);
  const dx = local.x / layout.scale.x;
  const dy = local.y / layout.scale.y;
  const clamp = (value: number, max: number): number => Math.round(Math.min(Math.max(0, max), Math.max(0, value)));

  const crop: Crop = { ...start.crop };
  if (handle.x === 0) crop.left = clamp(start.crop.left + dx, size.x - start.crop.right - 1);
  else if (handle.x === 1) crop.right = clamp(start.crop.right - dx, size.x - start.crop.left - 1);
  if (handle.y === 0) crop.top = clamp(start.crop.top + dy, size.y - start.crop.bottom - 1);
  else if (handle.y === 1) crop.bottom = clamp(start.crop.bottom - dy, size.y - start.crop.top - 1);

  const next: ItemTransform = { ...start, crop };
  const position = pinPoint(start, next, size, { x: 1 - handle.x, y: 1 - handle.y });
  return { crop, position };
}

/** Rotate about the alignment anchor by the angle the pointer swept around it. */
export function rotateTransform(start: ItemTransform, from: Vec2, to: Vec2, snap: boolean): Partial<ItemTransform> {
  const pivot = start.position;
  const a0 = Math.atan2(from.y - pivot.y, from.x - pivot.x);
  const a1 = Math.atan2(to.y - pivot.y, to.x - pivot.x);
  let rotation = start.rotation + ((a1 - a0) * 180) / Math.PI;
  if (snap) rotation = Math.round(rotation / ROTATION_SNAP_DEGREES) * ROTATION_SNAP_DEGREES;
  return { rotation: normalizeDegrees(rotation) };
}

export function moveTransform(start: ItemTransform, deltaParent: Vec2): Partial<ItemTransform> {
  return { position: { x: start.position.x + deltaParent.x, y: start.position.y + deltaParent.y } };
}
